import { z } from 'zod';
import type { IGameState, IStage } from '.';

/*
                                                        this section repeats for the 3 rounds,
                                                        then goes to the ending
                                                    .----------------------------------------------------.
                                                    |                                                    |
 *                                                  v                                                    v
 */
export type Stage = IStage | 'round-intro' | 'play-round-turns' | 'round-outro';

export const BoardPanelColors = ['yellow', 'red', 'green', 'blue', 'purple', 'orange'] as const;
export type BoardPanelColor = (typeof BoardPanelColors)[number];

export const PileTypes = ['goodCards', 'badCards', 'desireCards'] as const;
export type PileType = (typeof PileTypes)[number];

// Curated set of Lucide icon names usable in the preset icon-picker.
// The concrete icon components live in each client (lucide-static import).
export const pickableIconNames = [
	'Palette',
	'HandHeart',
	'Hourglass',
	'RefreshCcw',
	'RefreshCw',
	'Shield',
	'PartyPopper',
	'Rainbow',
	'Lock',
	'Unlock',
	'BookOpen',
	'Book',
	'Smile',
	'Frown',
	'Meh',
	'Target',
	'Puzzle',
	'Scale',
	'Handshake',
	'HandHelping',
	'HeartHandshake',
	'Heart',
	'HeartCrack',
	'Search',
	'MessageCircle',
	'MessageSquare',
	'Megaphone',
	'Sparkles',
	'Star',
	'TrendingUp',
	'TrendingDown',
	'Wrench',
	'Globe',
	'Zap',
	'ZapOff',
	'Ruler',
	'Lightbulb',
	'Brain',
	'Sun',
	'Cloud',
	'Flame',
	'Users',
	'User',
	'UsersRound',
	'Map',
	'MapPin',
	'Compass',
	'Rocket',
	'Check',
	'X',
	'Link',
	'Sprout',
	'Leaf',
	'Award',
	'Medal',
	'Trophy',
	'Crown',
	'Gift',
	'Clock',
	'Calendar',
	'CalendarCheck',
	'Anchor',
	'ThumbsUp',
	'ShieldHalf',
	'Eye',
	'EyeOff',
	'DoorOpen',
	'DoorClosed',
	'ArrowRightLeft',
	'CircleHelp',
	'Bolt',
	'Trash2',
	'GraduationCap',
	'Briefcase',
] as const;
export type PickableIconName = (typeof pickableIconNames)[number];

const JackpotCardSchema = z.object({ name: z.string(), icon: z.string().optional() });
export type JackpotCard = z.infer<typeof JackpotCardSchema>;

export const JackpotRoundConfigSchema = z.object({
	goodCards: z.array(JackpotCardSchema).optional(),
	badCards: z.array(JackpotCardSchema).optional(),
	desireCards: z.array(JackpotCardSchema).optional(),
});
export type JackpotRoundConfig = z.infer<typeof JackpotRoundConfigSchema>;

export const JackpotPresetSchema = z.object({
	presetName: z.string(),

	round1Title: z.string(),
	round2Title: z.string(),
	round3Title: z.string(),

	round1: JackpotRoundConfigSchema.optional(),
	round2: JackpotRoundConfigSchema.optional(),
	round3: JackpotRoundConfigSchema.optional(),
});
export type JackpotPreset = z.infer<typeof JackpotPresetSchema>;

export const JackpotDataSchema = z
	.object({
		goodCards: z.array(JackpotCardSchema).default([]),
		badCards: z.array(JackpotCardSchema).default([]),
		desireCards: z.array(JackpotCardSchema).default([]),
	})
	.default({ goodCards: [], badCards: [], desireCards: [] });

export type JackpotPlayerState = {
	id: string;
	/**
	 * when false this player will get skipped by game logic,
	 * allowing them to leave and come back at a later time without blocking the entire game
	 */
	active: boolean;
	/**
	 * when a player has 4 good cards and 4 bad cards they get a +2 score increase.
	 * This should only happend once, so we keep track of wether we granted the points here.
	 */
	name: string;
	color: string;
	score: number;
	roundData: {
		goodCardPileIndex: number;
		badCardPileIndex: number;
		desireCardPileIndex: number;
		lotteryTickets: JackpotCard[];
		goodCards: JackpotCard[];
		badCards: JackpotCard[];
		desireCards: JackpotCard[];
	}[];

	lastScreen: JackpotUpdate;
};

export type JackpotState = {
	stage: Stage;
	preset: JackpotPreset;
	roundIndex: number;
	turnIndex: number;
	lastTurnInRound: boolean;
	sabotagedJackpotSpots: string[];

	goodCardPile: JackpotCard[];
	badCardPile: JackpotCard[];
	desireCardPile: JackpotCard[];

	currentGameColor?: BoardPanelColor;

	players: JackpotPlayerState[];
	currentPlayerIndex: number;
};

export type Card = { name: string; icon?: string };

export type RoundPiles = {
	goodCards: Card[];
	badCards: Card[];
	desireCards: Card[];
};

export type JackpotRoundsPiles = Record<number, RoundPiles>;

export const JackpotUpdateSchema = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('show-login-screen'), failed: z.boolean().optional() }),
	z.object({ kind: z.literal('show-register-screen') }),
	z.object({ kind: z.literal('show-round-intro-screen'), round: z.number(), roundName: z.string() }),
	// the jackpot is an array of numbers because in later rounds there are multiple wheels
	z.object({
		kind: z.literal('show-round-outro-screen'),
		round: z.number(),
		roundName: z.string(),
		jackpot: z.array(z.string()),
		options: z.array(z.string()).optional(),
		score: z.number(),
	}),
	z.object({ kind: z.literal('show-wait-screen'), playerName: z.string() }),
	z.object({ kind: z.literal('show-turnstart-screen') }),
	z.object({
		kind: z.literal('show-swipecards-screen'),
		round: z.number(),
		roundTitle: z.string(),
		forColor: z.enum(['red', 'yellow', 'green']),
		options: z.array(z.object({ name: z.string(), icon: z.string().optional() })),
	}),
	z.object({ kind: z.literal('show-jackpot-guess-screen'), options: z.array(z.string()) }),
	z.object({
		kind: z.literal('show-randomevent-screen'),
		action: z.string(),
		chooseColor: z.boolean(),
		tickets: z.array(z.object({ name: z.string() })).optional(),
	}),
	z.object({ kind: z.literal('show-sabotage-screen'), options: z.array(z.string()) }),
	z.object({
		kind: z.literal('show-results-screen'),
		players: z.array(z.object({ name: z.string(), color: z.string(), score: z.number() })),
	}),

	z.object({
		kind: z.literal('update-players-state'),
		players: z.array(z.object({ name: z.string(), color: z.string() })),
	}),
	z.object({
		kind: z.literal('update-player-state'),
		player: z.object({
			//
			name: z.string(),
			color: z.string(),
			score: z.number(),
			goodCards: z.array(JackpotCardSchema),
			badCards: z.array(JackpotCardSchema),
			desireCards: z.array(JackpotCardSchema),
			lotteryTickets: z.array(JackpotCardSchema),
		}),
	}),

	z.object({ kind: z.literal('trigger-effect'), effect: z.enum(['confetti', 'red-flash']), text: z.string().optional() }),
]);
export type JackpotUpdate = z.infer<typeof JackpotUpdateSchema>;

export const JackpotAdminUpdateSchema = z.discriminatedUnion('kind', [
	//
	z.object({ kind: z.literal('update-state'), state: z.any() }),
]);
export type JackpotAdminUpdate = z.infer<typeof JackpotAdminUpdateSchema>;

export const JackpotMessageSchema = z.discriminatedUnion('kind', [
	// TODO: make it so this can only be sent by admin
	z.object({ kind: z.literal('start-game') }),
	z.object({ kind: z.literal('start-round') }),
	z.object({ kind: z.literal('end-round') }),
	z.object({ kind: z.literal('force-restart-round') }),
	z.object({ kind: z.literal('set-is-last-round'), lastRound: z.boolean() }),
	z.object({ kind: z.literal('set-player-active'), playerId: z.string(), active: z.boolean() }),

	// TODO: make it so this can be sent by any player
	z.object({ kind: z.literal('update-player-data'), playerId: z.string(), name: z.string(), color: z.string() }),
	z.object({ kind: z.literal('remove-player-data'), playerId: z.string() }),
	z.object({ kind: z.literal('choose-landing-spot'), playerId: z.string(), panelColor: z.enum(BoardPanelColors) }),
	z.object({
		kind: z.literal('choose-cards'),
		playerId: z.string(),
		cards: z.array(z.string()),
		swipedCount: z.number(),
	}),
	z.object({ kind: z.literal('choose-jackpot-landing'), playerId: z.string(), landing: z.string() }),
	z.object({ kind: z.literal('choose-sabotage-landing'), playerId: z.string(), landing: z.string() }),
]);
export type JackpotMessage = z.infer<typeof JackpotMessageSchema>;
